import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  Box,
  Button,
  Divider,
  Chip,
  CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Delete as DeleteIcon,
  AttachMoney as AttachMoneyIcon,
  People as PeopleIcon,
  MusicNote as MusicNoteIcon,
  CreditCard as CreditCardIcon,
  SystemUpdate as SystemUpdateIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { fetcher, mutation } from '../../utils/api';
import { ApiRoutes } from '../../constants/routes';

const NotificationDropdown = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isUploading, setIsUploading] = useState(() => localStorage.getItem('isUploading') === 'true');
  const queryClient = useQueryClient();
  const theme = useTheme();
  const open = Boolean(anchorEl);

  // Watch for localStorage changes to detect upload state
  useEffect(() => {
    const checkUploadState = () => {
      setIsUploading(localStorage.getItem('isUploading') === 'true');
    };
    
    // Check on mount and set up storage event listener
    checkUploadState();
    window.addEventListener('storage', checkUploadState);
    
    // Also poll periodically since storage events don't fire in same tab
    const interval = setInterval(checkUploadState, 1000);
    
    return () => {
      window.removeEventListener('storage', checkUploadState);
      clearInterval(interval);
    };
  }, []);

  // Fetch notifications
  const { data: notificationsData, isLoading } = useQuery(
    ['notifications'],
    () => fetcher(ApiRoutes.Notifications),
    {
      refetchInterval: isUploading ? false : 30000, // Disable polling during upload
      refetchOnWindowFocus: !isUploading, // Disable refetch on focus during upload
      enabled: !!localStorage.getItem('token') && !isUploading, // Only fetch if user is authenticated and not uploading
      staleTime: 0, // Always consider data stale to ensure fresh data on login
    }
  );

  // Mark notification as read mutation
  const markAsReadMutation = useMutation(
    (notificationId) => mutation(`${ApiRoutes.Notifications}${notificationId}/read/`, {}, 'POST'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications']);
      }
    }
  );

  // Mark all as read mutation
  const markAllReadMutation = useMutation(
    () => mutation(`${ApiRoutes.Notifications}mark-all-read/`, {}, 'POST'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications']);
      }
    }
  );

  // Delete notification mutation
  const deleteNotificationMutation = useMutation(
    (notificationId) => mutation(`${ApiRoutes.Notifications}${notificationId}/delete/`, {}, 'DELETE'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications']);
      }
    }
  );

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification) => {
    // Mark as read if not already read
    if (!notification.is_read) {
      markAsReadMutation.mutate(notification.id);
    }

    // Navigate to action URL if available
    if (notification.action_url) {
      window.location.href = notification.action_url;
    }

    handleClose();
  };

  const handleMarkAllRead = () => {
    markAllReadMutation.mutate();
  };

  const handleDeleteNotification = (notificationId, event) => {
    event.stopPropagation();
    deleteNotificationMutation.mutate(notificationId);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'follow':
        return <PeopleIcon sx={{ color: theme.palette.primary.main }} />;
      case 'like':
        return <MusicNoteIcon sx={{ color: theme.palette.primary.main }} />;
      case 'curator_earning':
        return <AttachMoneyIcon sx={{ color: theme.palette.primary.main }} />;
      case 'payout':
        return <CreditCardIcon sx={{ color: theme.palette.primary.main }} />;
      case 'credit_low':
      case 'credit_depleted':
        return <CreditCardIcon sx={{ color: theme.palette.warning.main }} />;
      case 'track_uploaded':
      case 'album_uploaded':
        return <MusicNoteIcon sx={{ color: theme.palette.primary.main }} />;
      default:
        return <SystemUpdateIcon sx={{ color: theme.palette.primary.main }} />;
    }
  };


  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const notifications = notificationsData?.notifications || [];
  const unreadCount = notificationsData?.unread_count || 0;

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{ color: theme.palette.mode === 'light' ? theme.palette.primary.main : 'white' }}
        aria-label="notifications"
      >
        <Badge badgeContent={unreadCount} color="error">
          {unreadCount > 0 ? (
            <NotificationsIcon />
          ) : (
            <NotificationsNoneIcon />
          )}
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 500,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.primary.main}33`
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontFamily: 'Inter', fontWeight: 'bold' }}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Button
                size="small"
                startIcon={<MarkEmailReadIcon />}
                onClick={handleMarkAllRead}
                disabled={markAllReadMutation.isLoading}
                sx={{ 
                  color: theme.palette.primary.main,
                  textTransform: 'none',
                  fontSize: '0.75rem'
                }}
              >
                Mark all read
              </Button>
            )}
          </Box>

          <Divider sx={{ mb: 2, borderColor: theme.palette.divider }} />

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} sx={{ color: theme.palette.primary.main }} />
            </Box>
          ) : notifications.length > 0 ? (
            <List sx={{ maxHeight: 350, overflow: 'auto' }}>
              {notifications.map((notification) => (
                <ListItem
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: notification.is_read ? 'transparent' : `${theme.palette.primary.main}1A`,
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? `${theme.palette.primary.main}33` 
                        : `${theme.palette.primary.main}1A`
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)' }}>
                      {getNotificationIcon(notification.type)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{
                            fontFamily: 'Inter',
                            fontWeight: notification.is_read ? 'normal' : 'bold',
                            color: notification.is_read ? theme.palette.text.secondary : theme.palette.text.primary
                          }}
                        >
                          {notification.title}
                        </Typography>
                        {notification.priority === 'urgent' && (
                          <Chip
                            label="Urgent"
                            size="small"
                            sx={{
                              backgroundColor: '#f44336',
                              color: 'white',
                              fontSize: '0.6rem',
                              height: 16
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{
                            display: 'block',
                            color: theme.palette.text.secondary,
                            fontSize: '0.75rem',
                            mb: 0.5
                          }}
                        >
                          {notification.message}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography
                            variant="caption"
                            component="span"
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            {formatTimeAgo(notification.created)}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => handleDeleteNotification(notification.id, e)}
                            disabled={deleteNotificationMutation.isLoading}
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </>
                    }
                    secondaryTypographyProps={{
                      component: 'div'
                    }}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <NotificationsNoneIcon sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 2 }} />
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                No notifications yet
              </Typography>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationDropdown;
